import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../components/context/authContext";
import { useNotifications } from "../../components/context/notificationContext";
import { isSuspendedFrom } from "../../utils/membersSiteAccessPermissions";
import eventBus from "../../utils/eventBus";
import Remote from "../../components/remote/remote";
import SkitAdCard from "../../components/skitAd/skitAdCard";
import ClapperBoard from "../../components/clapperBoard/animatedClapperBoard";
import {
  SmallPlainInsetBtnEventListener,
  GlassBtn,
} from "../../components/buttons/"; // Ensure this is the correct import path
import { MegaphoneIcon, StarIcon, LineChartIcon } from "../../components/icons";
import {
  apiDeleteSkitIdFromSourceIdAction,
  apiFetchTrendingSkits,
  apiFetchPopularSkits,
  apiReportSkit,
} from "../../api/backend/skits";
import {
  apiRecordUserSkitNotInterested,
  apiFetchUserSkitNotInterested,
} from "../../api/backend/user";
import UniversalSearchLauncher from "../../components/universalSearchBar/universalSearchLauncher";
import ReportModal from "../../components/reportModal/reportModal";
import Loader from "../../components/loader/loader";
import { HashLink } from "react-router-hash-link";
import "./topSkistListsPage.scss";

const TopSkitsListsPage = () => {
  const {
    currentMemberId,
    handleLogout,
    openSkitUploadForm,
    setSuccessFlashMessage,
    setNotInterested,
  } = useAuth();
  const { notificationsCountFetchedFromMiddleware, notInterested } =
    useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [isClapping, setIsClapping] = useState(false);
  const [trendingSkitsData, setTrendingSkitsData] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [popularSkitsData, setPopularSkitsData] = useState([]);
  const [colorPaletteVisible, setColorPaletteVisible] = useState(false);
  const [sticky, setSticky] = useState(false);
  const [error, setError] = useState("");
  const [loadingTrending, setLoadingTrending] = useState(false);
  const [loadingPopular, setLoadingPopular] = useState(false);
  const [reportingSkit, setReportingSkit] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const initialTrendingOffset =
    parseInt(localStorage.getItem("trendingOffset")) || 0;
  const [trendingOffset, setTrendingOffset] = useState(initialTrendingOffset);
  const initialPopularOffset =
    parseInt(localStorage.getItem("popularOffset")) || 0;
  const [popularOffset, setPopularOffset] = useState(initialPopularOffset);
  const maxSkitsPerCategory = 6;

  const onLogoutClick = () => {
    handleLogout();
  };

  const fetchCurrentUserNotInterestedActivityLogs = useCallback(async () => {
    try {
      const response = await apiFetchUserSkitNotInterested({
        loggedInAccountHolderId: currentMemberId, // was sourceId
      });
      // suspension handling
      if (isSuspendedFrom(response) || isSuspendedFrom(response?.data)) {
        await handleLogout();
        return;
      }
      // 🔔 hydrate badge (tolerant to resp/body)
      const count =
        typeof response?.data?.unreadNotificationCount === "number"
          ? response.data.unreadNotificationCount
          : typeof response?.unreadNotificationCount === "number"
            ? response.unreadNotificationCount
            : null;
      if (typeof count === "number") {
        notificationsCountFetchedFromMiddleware(count);
      }
      // shape-tolerant parsing
      const body = response?.data !== undefined ? response.data : response;
      const list = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body)
          ? body
          : [];
      setNotInterested(list.map((log) => log.relatedId));
    } catch (error) {
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout();
        return;
      }
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware(errCount);
      }
      console.error("Failed to fetch 'not interested' logs:", error);
      setError("Failed to load 'not interested' logs");
    } finally {
      // (intentionally empty to match your original)
    }
  }, [currentMemberId, notificationsCountFetchedFromMiddleware, handleLogout]);

  useEffect(() => {
    fetchCurrentUserNotInterestedActivityLogs();
  }, [fetchCurrentUserNotInterestedActivityLogs]);

  const handleNotInterested = useCallback(
    async (skit) => {
      try {
        const response = await apiRecordUserSkitNotInterested({
          loggedInAccountHolderId: currentMemberId,
          activity: "notInterested",
          relatedId: skit.id,
          targetId: skit.sourceId,
        });
        // suspension eject
        if (isSuspendedFrom(response) || isSuspendedFrom(response?.data)) {
          await handleLogout();
          return;
        }
        // 🔔 hydrate badge if backend included a count
        const count = response?.data?.unreadNotificationCount;
        if (typeof count === "number") {
          notificationsCountFetchedFromMiddleware(count);
        }
        if (response?.data?.success) {
          setNotInterested((prev) => [...prev, skit.id]);
        }
      } catch (error) {
        // suspension on error path too
        if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
          await handleLogout();
          return;
        }
        // 🔔 hydrate badge from error payload if present
        const errCount =
          error?.response?.data?.unreadNotificationCount ??
          error?.unreadNotificationCount ??
          null;
        if (typeof errCount === "number") {
          notificationsCountFetchedFromMiddleware(errCount);
        }
        console.error("Error marking skit as not interested:", error);
        setError("Failed to mark skit as not interested");
      }
    },
    [currentMemberId, notificationsCountFetchedFromMiddleware, handleLogout]
  );

  // refresh both feeds after redirect back from the upload page
  useEffect(() => {
    if (location.state?.refreshFromUpload) {
      setTrendingOffset(0);
      setPopularOffset(0);
      setTrendingSkitsData([]);
      setPopularSkitsData([]);
      // re-fetch “page 1” with current category (or all)
      (async () => {
        await Promise.all([
          apiFetchTrendingSkits(0, maxSkitsPerCategory, activeCategory).then(
            (r) => r?.data?.success && setTrendingSkitsData(r.data.data)
          ),
          apiFetchPopularSkits(0, maxSkitsPerCategory, activeCategory).then(
            (r) => r?.data?.success && setPopularSkitsData(r.data.data)
          ),
        ]);
      })();
      // clear the state flag so back/forward doesn’t retrigger
      navigate(".", { replace: true, state: null });
    }
  }, [location.state, activeCategory, navigate]);

  const handleDeleteSourceIdRelatedIdSkit = (skitId) => {
    apiDeleteSkitIdFromSourceIdAction({ skitId })
      .then((response) => {
        if (response.status === 200) {
          const updatedTrendingSkits = trendingSkitsData.filter(
            (skit) => skit.id !== skitId
          );
          const updatedPopularSkits = popularSkitsData.filter(
            (skit) => skit.id !== skitId
          );
          setTrendingSkitsData(updatedTrendingSkits);
          setPopularSkitsData(updatedPopularSkits);
        } else {
          alert("Failed to delete the skit");
        }
      })
      .catch((error) => {
        console.error("Error deleting skit:", error);
        alert("An error occurred while deleting the skit");
      });
  };

  const toggleColorPaletteVisibility = () => {
    setColorPaletteVisible(!colorPaletteVisible);
  };

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    setSticky(scrollTop > 100);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const fetchTrendingSkits = async (offset, categoryId = null) => {
    setLoadingTrending(true);
    try {
      const response = await apiFetchTrendingSkits(
        offset,
        maxSkitsPerCategory,
        categoryId
      );
      if (response.status === 200 && response.data.success) {
        const fetchedSkits = response.data.data;
        setTrendingSkitsData((prevSkits) =>
          offset === 0 ? fetchedSkits : [...prevSkits, ...fetchedSkits]
        );
      }
    } catch (error) {
      console.error("Error fetching skits:", error);
    } finally {
      setLoadingTrending(false);
    }
  };

  const fetchPopularSkits = async (offset, categoryId = null) => {
    setLoadingPopular(true);
    try {
      const response = await apiFetchPopularSkits(
        offset,
        maxSkitsPerCategory,
        categoryId
      );
      if (response.status === 200 && response.data.success) {
        const fetchedSkits = response.data.data;
        setPopularSkitsData((prevSkits) =>
          offset === 0 ? fetchedSkits : [...prevSkits, ...fetchedSkits]
        );
      }
    } catch (error) {
      console.error("Error fetching skits:", error);
    } finally {
      setLoadingPopular(false);
    }
  };
  const handleViewMoreTrending = async (event) => {
    event.preventDefault(); // Prevent default action
    setIsClapping(true);
    setLoadingTrending(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate delay for animation
      const newOffset = trendingOffset + maxSkitsPerCategory;
      setTrendingOffset(newOffset);
      await fetchTrendingSkits(newOffset, activeCategory); // Fetch more skits with the new offset
    } catch (error) {
      console.error("Failed to load more skits:", error);
    } finally {
      setIsClapping(false);
      setLoadingTrending(false);
    }
  };

  const handleViewMorePopular = async (e) => {
    e.preventDefault(); // Prevent default action
    setIsClapping(true);
    setLoadingPopular(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate delay for animation
      const newOffset = popularOffset + maxSkitsPerCategory;
      setPopularOffset(newOffset);
      await fetchPopularSkits(newOffset, activeCategory); // Fetch more skits with the new offset
    } catch (error) {
      console.error("Failed to load more skits:", error);
    } finally {
      setIsClapping(false);
      setLoadingPopular(false);
    }
  };
  const handleViewMoreAll = async (e) => {
    e.preventDefault(); // Prevent default action
    setIsClapping(true);
    setLoadingTrending(true);
    setLoadingPopular(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate delay for animation
      // Fetch more trending skits
      const newTrendingOffset = trendingOffset + maxSkitsPerCategory;
      setTrendingOffset(newTrendingOffset);
      await fetchTrendingSkits(newTrendingOffset, activeCategory);
      // Fetch more popular skits
      const newPopularOffset = popularOffset + maxSkitsPerCategory;
      setPopularOffset(newPopularOffset);
      await fetchPopularSkits(newPopularOffset, activeCategory);
    } catch (error) {
      console.error("Failed to load more skits:", error);
    } finally {
      setIsClapping(false);
      setLoadingTrending(false);
      setLoadingPopular(false);
    }
  };

  const reportSkit = async (choices) => {
    // tiny inline flash helper
    const flash = (msg, ms = 1800) => {
      setSuccessFlashMessage?.(msg);
      clearTimeout(reportSkit._t);
      reportSkit._t = setTimeout(() => setSuccessFlashMessage?.(null), ms);
    };
    try {
      const res = await apiReportSkit({
        loggedInAccountHolderId: currentMemberId, // ← keep nomenclature
        skitId: reportingSkit.id,
        reason: JSON.stringify(choices),
      });
      // eject if backend signals suspension
      if (isSuspendedFrom(res) || isSuspendedFrom(res?.data)) {
        await handleLogout?.();
        return;
      }
      // 🔔 hydrate unread count (body first, then header)
      const bodyCount = Number(
        res?.data?.unreadNotificationCount ?? res?.unreadNotificationCount
      );
      if (Number.isFinite(bodyCount))
        notificationsCountFetchedFromMiddleware?.(bodyCount);
      const headerCount = Number(res?.headers?.["x-unread-notifications"]);
      if (Number.isFinite(headerCount))
        notificationsCountFetchedFromMiddleware?.(headerCount);
      // flash success (no alert)
      flash("Report sent ✅");
      // if backend suspended it, re-load page 1 of your “live” feed
      const skitStatus = res?.data?.data?.skitStatus ?? res?.data?.skitStatus;
      if (skitStatus === "suspended") {
        // e.g. reset offsets & re-fetch
        setTrendingOffset(0);
        setPopularOffset(0);
        fetchTrendingSkits(0, activeCategory);
        fetchPopularSkits(0, activeCategory);
      }
    } catch (error) {
      // suspension on error path too
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout?.();
        return;
      }

      // 🔔 hydrate count from error payload if present
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware?.(errCount);
      }

      flash("Could not submit report. Try again.", 2200);
    } finally {
      setReportingSkit(null);
    }
  };

  const updateSkitsData = useCallback(async () => {
    try {
      const updatedTrendingSkits = await apiFetchTrendingSkits(
        0,
        maxSkitsPerCategory,
        activeCategory
      );
      const updatedPopularSkits = await apiFetchPopularSkits(
        0,
        maxSkitsPerCategory,
        activeCategory
      );
      if (
        updatedTrendingSkits.status === 200 &&
        updatedTrendingSkits.data.success
      ) {
        setTrendingSkitsData(updatedTrendingSkits.data.data);
      }
      if (
        updatedPopularSkits.status === 200 &&
        updatedPopularSkits.data.success
      ) {
        setPopularSkitsData(updatedPopularSkits.data.data);
      }
    } catch (error) {
      console.error("Error updating skits:", error);
    } finally {
    }
  }, [maxSkitsPerCategory, activeCategory]);

  useEffect(() => {
    if (activeCategory === null) {
      // Fetch skits for all categories when no specific category is selected
      fetchTrendingSkits(trendingOffset);
      fetchPopularSkits(popularOffset);
    } else {
      // Fetch skits for the selected category
      fetchTrendingSkits(trendingOffset, activeCategory);
      fetchPopularSkits(popularOffset, activeCategory);
    }
  }, [trendingOffset, popularOffset, activeCategory]);

  useEffect(() => {
    const handleSkitPromotion = (event, promotedSkit) => {
      setTrendingSkitsData((prevSkits) => {
        const updatedSkits = [
          promotedSkit,
          ...prevSkits.filter((skit) => skit.id !== promotedSkit.id),
        ];
        return updatedSkits;
      });
      setPopularSkitsData((prevSkits) => {
        const updatedSkits = [
          promotedSkit,
          ...prevSkits.filter((skit) => skit.id !== promotedSkit.id),
        ];
        return updatedSkits;
      });
    };
    eventBus.on("skitPromoted", handleSkitPromotion);
    return () => {
      eventBus.off("skitPromoted", handleSkitPromotion);
    };
  }, []);

  const renderSkits = (skitsData, skitType) => {
    return skitsData
      .filter((skit) => !notInterested.includes(skit.id))
      .map((skit) => (
        <SkitAdCard
          key={skit.id}
          specificSkitData={skit}
          currentMemberId={currentMemberId}
          onDelete={() => handleDeleteSourceIdRelatedIdSkit(skit.id)}
          onNotInterested={() => handleNotInterested(skit)}
          updateSkitsData={updateSkitsData}
          skitType={skitType} // Pass the skit type to each Skit component
          isUploadVideoFormOpen={openSkitUploadForm} // Pass the form state to manage z-index
          onBlowWhistle={(skitToReport) => setReportingSkit(skitToReport)}
        />
      ));
  };
  const noChannelSelected = useCallback(async () => {
    try {
      const trendingResponse = await apiFetchTrendingSkits(
        0,
        maxSkitsPerCategory
      );
      const popularResponse = await apiFetchPopularSkits(
        0,
        maxSkitsPerCategory
      );

      if (trendingResponse.status === 200 && trendingResponse.data.success) {
        setTrendingSkitsData(trendingResponse.data.data);
      }
      if (popularResponse.status === 200 && popularResponse.data.success) {
        setPopularSkitsData(popularResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching skits when no channel is selected:", error);
    } finally {
    }
  }, [maxSkitsPerCategory]);

  useEffect(() => {
    if (activeCategory === null) {
      // Fetch skits for all categories when no specific category is selected
      noChannelSelected();
    } else {
      // Fetch skits for the selected category
      fetchTrendingSkits(trendingOffset, activeCategory);
      fetchPopularSkits(popularOffset, activeCategory);
    }
  }, [trendingOffset, popularOffset, activeCategory, noChannelSelected]);

  const scrollWithOffset = (el, offset) => {
    const elementPosition = el.getBoundingClientRect().top;
    const scrollY = window.scrollY;
    const offsetPosition = elementPosition + scrollY - offset;
    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  return (
    <div id="topSkitsListingPage">
      <div
        className={`${colorPaletteVisible ? "glassShelf" : "topControlsWrapper"} ${sticky ? "sticky" : "topControlsWrapper"}`}
      >
        <Remote
          onStartSkitUploadFlowClick={(statePayload) =>
            navigate("/upload-skit", { state: statePayload })
          }
          toggleColorPaletteVisibility={toggleColorPaletteVisibility}
          colorPaletteVisible={colorPaletteVisible}
          setColorPaletteVisible={setColorPaletteVisible}
          onChannelChange={(categoryId) => {
            setActiveCategory(categoryId);
            setTrendingOffset(0);
            setPopularOffset(0);
          }}
          onClick={onLogoutClick}
        />
        <UniversalSearchLauncher />
      </div>
      <div className="tabs">
        <GlassBtn
          glassBtnImage={
            <LineChartIcon
              height="25px"
              width="25px"
              fillColor={activeTab === "trending" ? "#fff" : "#e2b645"} // Dynamic fillColor
            />
          }
          className={
            activeTab === "trending" ? "active-button" : "inactive-button"
          }
          extraStyles={{
            height: "20px",
          }}
          glassBtnText="Trending"
          glassBtnTextFontSize="1.3rem"
          glassBtnFontWeight="900"
          glassBtnFontColor={activeTab === "trending" ? "#fff" : "#e2b645"}
          glassBtnTextDecorationColor={
            activeTab === "trending" ? "#fff" : "#e2b645"
          }
          onClick={(e) => {
            e.preventDefault();
            setActiveTab("trending");
          }}
        />
        <GlassBtn
          glassBtnImage={
            <StarIcon
              height="25px"
              width="25px"
              fillColor={activeTab === "popular" ? "#fff" : "#fdfd96"} // Dynamic fillColor
            />
          }
          className={
            activeTab === "popular" ? "active-button" : "inactive-button"
          }
          extraStyles={{
            height: "20px",
          }}
          glassBtnText="Popular"
          glassBtnTextFontSize="1.3rem"
          glassBtnFontWeight="900"
          glassBtnFontColor={activeTab === "popular" ? "#fff" : "#fdfd96"}
          glassBtnTextDecorationColor={
            activeTab === "popular" ? "#fff" : "#fdfd96"
          }
          onClick={(e) => {
            e.preventDefault();
            setActiveTab("popular");
          }}
        />
        <HashLink
          to={`/top-100-skits/${currentMemberId}/top-100-skits-billboard#skits-ranking`}
          scroll={(el) => scrollWithOffset(el, 50)}
          style={{ textDecoration: "none" }}
        >
          <GlassBtn
            glassBtnImage={
              <LineChartIcon height="25px" width="25px" fillColor="#e2b645" />
            }
            extraStyles={{
              height: "20px",
            }}
            glassBtnText="Top Skits Charts"
            glassBtnTextFontSize="1.3rem"
            glassBtnFontWeight="900"
            glassBtnFontColor="#e2b645"
            glassBtnTextDecorationColor="#e2b645"
          />
        </HashLink>
      </div>
      <div className="top-skits-grid">
        {activeTab === "trending" && (
          <>
            {renderSkits(trendingSkitsData, "Trending")}
            {trendingSkitsData.length > 0 && (
              <div className="View-More-Action">
                {loadingTrending && (
                  <Loader className=" activeTab-more-trending-loader" />
                )}
                <ClapperBoard isClapping={isClapping} />
                <SmallPlainInsetBtnEventListener
                  image={
                    <MegaphoneIcon
                      height="20px"
                      width="20px"
                      style={{ marginRight: "3px" }}
                    />
                  }
                  text="Trending Action"
                  width="188px"
                  style={{
                    fontSize: "1.25rem",
                  }}
                  onClick={(e) => handleViewMoreTrending(e)} // Pass the event to the handler
                />
              </div>
            )}
          </>
        )}
        {activeTab === "popular" && (
          <>
            {renderSkits(popularSkitsData, "Popular")}
            {popularSkitsData.length > 0 && (
              <div className="View-More-Action">
                {loadingPopular && (
                  <Loader className=" activeTab-more-popular-loader" />
                )}
                <ClapperBoard isClapping={isClapping} />
                <SmallPlainInsetBtnEventListener
                  image={
                    <MegaphoneIcon
                      height="20px"
                      width="20px"
                      style={{ marginRight: "3px" }}
                    />
                  }
                  text="Popular Action"
                  width="188px"
                  style={{
                    fontSize: "1.25rem",
                  }}
                  onClick={(e) => handleViewMorePopular(e)} // Pass the event to the handler
                />
              </div>
            )}
          </>
        )}
        {reportingSkit && (
          <ReportModal
            onBlowWhistle={reportSkit}
            kind="skit"
            onCancel={() => setReportingSkit(null)}
          />
        )}
      </div>
    </div>
  );
};

export default TopSkitsListsPage;
